import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import {
  type ApiError,
  addActionNameToMutationResult,
  handleZodError,
  useAxiosInstance,
} from 'shared/hooks/utils';
import { z } from 'zod/v4';

import { AutomationOperatorSchema } from '../pages/AutomationWizard/controller/operators';
import {
  AutomationModelSchema,
  AutomationTriggerSchema,
} from '../pages/AutomationWizard/controller/types';

const CreateAutomationResponseSchema = z.object({
  message: z.string(),
});

const ActionNullSchema = z.object({
  id: z.string().optional(),
  type: z.null(),
  _destroy: z.boolean().optional(),
});

const AutomationActionSchema = z.discriminatedUnion('type', [
  z.object({
    id: z.string().optional(),
    type: z.literal('CopyValueToField'),
    value: z.union([z.boolean(), z.string(), z.number(), z.null()]),
    destinationField: z.string(),
    _destroy: z.boolean().optional(),
  }),
  z.object({
    id: z.string().optional(),
    type: z.literal('CopyFieldToField'),
    sourceField: z.string().nullable(),
    destinationField: z.string().nullable(),
    _destroy: z.boolean().optional(),
  }),
  z.object({
    id: z.string().optional(),
    type: z.literal('AssignRecoveryPlan'),
    assignableType: z.string().default('RecoveryPlan'),
    assignableId: z.coerce.string(),
    _destroy: z.boolean().optional(),
  }),
  z.object({
    id: z.string().optional(),
    type: z.literal('AssignAccountManager'),
    assignableType: z.string().default('User'),
    assignableId: z.coerce.string(),
    _destroy: z.boolean().optional(),
  }),
  z.object({
    id: z.string().optional(),
    type: z.literal('Pause'),
    assignableType: z.string().default('Reason'),
    assignableId: z.coerce.string(),
    value: z.coerce.string().nullable().default(null),
    _destroy: z.boolean().optional(),
  }),
  z.object({
    id: z.string().optional(),
    type: z.literal('Unpause'),
    _destroy: z.boolean().optional(),
  }),
]);

const ActionSchema = z.union([ActionNullSchema, AutomationActionSchema]);

const ConditionsSchema = z.object({
  id: z.string().optional(),
  fieldName: z.string().nullable(),
  operator: AutomationOperatorSchema.nullable(),
  operand: z.string().or(z.number()).nullable(),
  _destroy: z.literal(true).optional(),
});

export const AutomationSchema = z.object({
  id: z.string().optional(),
  name: z.string(),
  active: z.boolean(),
  description: z.string().nullable(),
  targetModel: AutomationModelSchema,
  triggers: z.array(AutomationTriggerSchema),
  conditions: z.array(ConditionsSchema),
  actions: z.array(ActionSchema),
});

export const CreateAutomationVariablesSchema = AutomationSchema.transform(data => {
  const { actions, ...rest } = data;
  return {
    ...rest,
    actions: actions.map(action => ActionSchema.parse(action)),
  };
});

export type CreateAutomationVariables = z.infer<typeof AutomationSchema>;
export type AutomationAction = z.infer<typeof ActionSchema>;
export type AutomationCondition = z.infer<typeof ConditionsSchema>;
type AutomationResponse = z.infer<typeof CreateAutomationResponseSchema>;

export async function createAutomationFn(
  axiosInstance: Promise<AxiosInstance>,
  data: CreateAutomationVariables,
) {
  const instance = await axiosInstance;

  const safeParse = CreateAutomationVariablesSchema.safeParse(data);
  if (!safeParse.success) handleZodError(safeParse, 'automation');

  const response = await instance.post('automations', CreateAutomationVariablesSchema.parse(data));

  return CreateAutomationResponseSchema.parse(response.data);
}

export function useCreateAutomation() {
  const client = useQueryClient();
  const axiosInstance = useAxiosInstance();

  const mutation = useMutation<AutomationResponse, ApiError, CreateAutomationVariables>({
    mutationFn: data => createAutomationFn(axiosInstance, data),
    onSuccess: () => client.invalidateQueries({ queryKey: ['automationIndex'] }),
  });

  return addActionNameToMutationResult<
    AutomationResponse,
    ApiError,
    CreateAutomationVariables,
    'createAutomation'
  >('createAutomation', mutation);
}
